import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import { Card, Button } from '../primitives';
import { useTranslation } from '../../hooks/useTranslation';
import { useOutletContext } from 'react-router-dom';
import Refresh from '../primitives/Refresh';

export default function CustomerInvoices({ invoices, getInvoiceData }) {
    const t = useTranslation();
    const { mobile } = useOutletContext();
    const formatCurrency = (amount, currency) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(amount / 100); // Stripe amounts are in cents
    };

    const getStatusBadge = (status) => {
        const statusColors = {
            draft: 'secondary',
            open: 'warning',
            paid: 'success',
            void: 'danger',
            uncollectible: 'danger'
        };
        return (
            <span className={`badge bg-${statusColors[status] || 'info'}`}>
                {status.charAt(0).toUpperCase() + status.slice(1)}
            </span>
        );
    };

    const handleInvoiceClick = (invoice) => {
        if (invoice.hosted_invoice_url) {
            window.open(invoice.hosted_invoice_url, '_blank');
        }
    };
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentInvoices = invoices.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(invoices.length / itemsPerPage);

    // Memoize the empty rows calculation
    const emptyRows = useMemo(() => {
        const rows = [];
        for (let i = currentInvoices.length; i < itemsPerPage; i++) {
            rows.push(i);
        }
        return rows;
    }, [currentInvoices.length]);

    // Memoize the current page data to prevent unnecessary re-renders
    const pageData = useMemo(() => ({
        currentInvoices,
        emptyRows,
        totalPages
    }), [currentInvoices, emptyRows, totalPages]);

    const [refreshing, setRefreshing] = useState(false);
    const [showCheckmark, setShowCheckmark] = useState(false);

    const handleRefresh = async () => {
        if (refreshing || showCheckmark) return;
        setRefreshing(true);
        await getInvoiceData();
        setShowCheckmark(true);
        setTimeout(() => {
            setRefreshing(false);
            setShowCheckmark(false);
        }, 10000);
    };

    return (
        <Card className='margin-bottom'>
            <Card.Header>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <h2>{t('Transactions')}</h2>
                    <Refresh handleRefresh={handleRefresh} showCheckmark={showCheckmark} refreshing={refreshing} />
                </div>
            </Card.Header>
            <Card.Body>
                <div className="transactions-list">
                    <div className="transactions-header">
                        <div className="transaction-row-item">{t('Invoice Number')}</div>
                        {!mobile && <div className="transaction-row-item">{t('Amount')}</div>}
                        {!mobile && <div className="transaction-row-item">{t('Status')}</div>}
                        <div className="transaction-row-item">{t('Date')}</div>
                    </div>
                    <div className="transactions-body">
                        {pageData.currentInvoices.map((invoice) => (
                            <div key={invoice.id} className={`transaction-row ${invoice.hosted_invoice_url ? 'invoice-link' : ''}`} onClick={() => handleInvoiceClick(invoice)}>
                                <div className="transaction-row-item">{invoice.number || 'N/A'}</div>
                                {!mobile && <div className="transaction-row-item">{formatCurrency(invoice.total, invoice.currency)}</div>}
                                {!mobile && <div className="transaction-row-item">{getStatusBadge(invoice.status)}</div>}
                                <div className="transaction-row-item">{format(new Date(invoice.created * 1000), 'MMM dd, yyyy')}</div>
                            </div>
                        ))}
                        {pageData.emptyRows.map((i) => (
                            <div key={`empty-${i}`} className="transaction-row">
                                <div className="transaction-row-item">-</div>
                                {!mobile && <div className="transaction-row-item">-</div>}
                                {!mobile && <div className="transaction-row-item">-</div>}
                                <div className="transaction-row-item">-</div>
                            </div>
                        ))}
                    </div>
                    {pageData.totalPages > 1 && (
                        <div className='transactions-footer'>
                            <Button
                                style={{ minWidth: '100px', margin: 0 }}
                                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                disabled={currentPage === 1}
                            >
                                {t('Previous')}
                            </Button>
                            <Button
                                style={{ minWidth: '100px', margin: 0 }}
                                onClick={() => setCurrentPage(prev => Math.min(prev + 1, pageData.totalPages))}
                                disabled={currentPage === pageData.totalPages}
                            >
                                {t('Next')}
                            </Button>
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};